import { ApolloCache } from '@apollo/client';
import {
  AgentComplianceReviewDocumentDocument,
  useAgentComplianceReviewIgnorePlatformRequirementIssueMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  AgentComplianceReviewDocumentQuery,
  AgentComplianceReviewPlatformRequirementIssueInput,
  PlatformRequirementIssueStatus,
  ValidationReviewSource,
} from '@client/graphql/__generated__/types';
import { useCallback } from 'react';

interface UseAgentComplianceReviewIgnorePlatformRequirementIssueArgs {
  /** Share-link token (`?token=` query param). */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

export type IgnorePlatformRequirementIssueArgs = Omit<
  AgentComplianceReviewPlatformRequirementIssueInput,
  'token' | 'ingestionFolderUuid' | 'isHidden' | 'message'
> & {
  ingestionDocumentId: string;
  isHidden: boolean;
  /**
   * The agent's reason for ignoring: text to store it, `null` to clear a reason
   * left by an earlier ignore, omitted to leave whatever is stored alone (what
   * Undo does).
   *
   * `null` cannot travel on the wire — codegen maps nullable inputs to
   * `T | undefined` (see `maybeValue` in `graphql/codegen.ts`), so an explicit
   * null is indistinguishable from omission by the time it reaches the server.
   * It is sent as an empty string instead, which the document service normalizes
   * back to null before writing.
   */
  message?: string | null;
};

interface UseAgentComplianceReviewIgnorePlatformRequirementIssueResult {
  loading: boolean;
}

/**
 * Mirrors the server's three-state reason contract into the cache so the hover
 * tooltip is right before the next real fetch confirms it: an un-ignore drops the
 * reason, an omitted message leaves the stored one alone, `''` means the agent
 * cleared it, and text replaces it.
 */
const patchedIgnoredReason = (
  input: Pick<
    AgentComplianceReviewPlatformRequirementIssueInput,
    'isHidden' | 'message'
  >,
  storedReason: string | null | undefined
): string | undefined => {
  if (input.isHidden !== true) {
    return undefined;
  }
  if (input.message === undefined) {
    return storedReason ?? undefined;
  }

  return input.message === '' ? undefined : input.message;
};

const patchPlatformRequirementIssueStatus = (
  cache: ApolloCache<unknown>,
  input: AgentComplianceReviewPlatformRequirementIssueInput & {
    ingestionDocumentId: string;
  }
): void => {
  cache.updateQuery<AgentComplianceReviewDocumentQuery>(
    {
      query: AgentComplianceReviewDocumentDocument,
      variables: {
        input: {
          token: input.token,
          ingestionFolderUuid: input.ingestionFolderUuid,
          ingestionDocumentId: input.ingestionDocumentId,
        },
      },
    },
    (data) => {
      const detail = data?.agentComplianceReviewDocument;
      if (!detail) {
        return data;
      }

      return {
        ...data,
        agentComplianceReviewDocument: {
          ...detail,
          platformRequirementIssues: detail.platformRequirementIssues.map(
            (issue) =>
              issue.publicIssueId === input.publicIssueId
                ? {
                    ...issue,
                    status: input.isHidden
                      ? PlatformRequirementIssueStatus.IGNORED_BY_AGENT
                      : PlatformRequirementIssueStatus.OPEN,
                    ignoredBySource: input.isHidden
                      ? ValidationReviewSource.AGENT
                      : undefined,
                    ignoredByAgentName: undefined,
                    ignoredAt: input.isHidden ? new Date() : undefined,
                    ignoredReason: patchedIgnoredReason(
                      input,
                      issue.ignoredReason
                    ),
                  }
                : issue
          ),
        },
      };
    }
  );
};

/**
 * Wraps the token-bound checklist mismatch ignore mutation. Binds the
 * share-link token + folder uuid so callers only pass the public issue id and
 * optional hidden state.
 *
 * Like validation suggestion ignores, this patches Apollo cache in place rather
 * than refetching the heavy document-detail query.
 */
export const useAgentComplianceReviewIgnorePlatformRequirementIssue = ({
  token,
  ingestionFolderUuid,
}: UseAgentComplianceReviewIgnorePlatformRequirementIssueArgs): [
  (args: IgnorePlatformRequirementIssueArgs) => Promise<void>,
  UseAgentComplianceReviewIgnorePlatformRequirementIssueResult,
] => {
  const [mutate, { loading }] =
    useAgentComplianceReviewIgnorePlatformRequirementIssueMutation();

  const ignore = useCallback(
    async (args: IgnorePlatformRequirementIssueArgs): Promise<void> => {
      // Never fire the write with empty-string fallback identifiers — a missing
      // token/folder is a not-ready state, not a valid request to send.
      if (
        token == null ||
        token === '' ||
        ingestionFolderUuid == null ||
        ingestionFolderUuid === ''
      ) {
        return;
      }
      const { ingestionDocumentId, message, ...mutationArgs } = args;
      // null (clear the reason) becomes '' because the generated input cannot
      // express null; omission still means "leave it alone".
      const messageVariable =
        message !== undefined ? { message: message ?? '' } : {};
      await mutate({
        variables: {
          input: {
            token,
            ingestionFolderUuid,
            ...mutationArgs,
            ...messageVariable,
          },
        },
        update: (cache) =>
          patchPlatformRequirementIssueStatus(cache, {
            token,
            ingestionFolderUuid,
            ingestionDocumentId,
            ...mutationArgs,
            ...messageVariable,
          }),
      });
    },
    [mutate, token, ingestionFolderUuid]
  );

  return [ignore, { loading }];
};
